import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus} from 'platform/components';
import {Box, useDevice} from 'platform/foundation';
import {useTheme} from 'styled-components';

import {isEmpty, isNil, isNotEmpty, isNotNil, sortBy} from 'ramda';

import {Nullish, suffixTestId, TestIdProps, useQueryState} from 'shared/web';

import {DesktopPagination} from '../../components/Pagination/DesktopPagination';
import {MobilePaginationWithFilterBar} from '../../components/Pagination/MobilePaginationWithFilterBar';
import {VehicleInfo} from '../../components/VehicleInfo/VehicleInfo';
import {useFilter} from '../../hooks/FilterContext';
import {useCatalogue} from '../../hooks/useCatalogue';
import {usePriceReport} from '../../hooks/usePriceReport';
import {usePriceReportSearchParams} from '../../hooks/usePriceReportSearchParams';
import {useVehicleTranslatedInfo} from '../../hooks/useVehicleTranslatedInfo';
import i18n from '../../i18n/i18n';
import {
  useGetAvailableCarsQuery,
  useGetPriceReportCarsDetailQuery,
} from '../../store/priceReportApi';
import {AvailableCar} from '../../types/AvailableCar';
import {QueryStateKeys} from '../../utils/routing';
import {AdvertisementColumnLayout} from './components/AdvertisementColumnLayout';
import {AdvertisementRowLayout} from './components/AdvertisementRowLayout';
import {DirectionSwitch} from './components/DirectionSwitch';
import {PAGE_SIZE} from './consts/pageSize';
import {AvailableCarWithLogo} from './types/available-car-with-logo';
import {formatMyVehicle} from './utils/formatMyVehicle';
import {getAvailableCarMarketPosition} from './utils/getAvailableCarMarketPosition';

export function AdvertisementPage(props: TestIdProps) {
  const {priceReport} = usePriceReport();
  const catalogue = useCatalogue();
  const {filter, errors: filterErrors} = useFilter();
  const device = useDevice();
  const {direction, setDirection, page, setPage} = usePriceReportSearchParams();
  const theme = useTheme();
  const hasServersSelected = isNil(filter?.server) || !isEmpty(filter.server);

  const [debug] = useQueryState(QueryStateKeys.Debug);
  const isDebug = debug === 'true';
  const {data: neighbours, isLoading: areNeighboursLoading} = useGetPriceReportCarsDetailQuery(
    isNotNil(priceReport) &&
      isNotNil(priceReport.neighborAdIds) &&
      isNotEmpty(priceReport.neighborAdIds)
      ? {reportId: priceReport.id, vehicleIds: priceReport.neighborAdIds}
      : skipToken,
    {skip: !isDebug}
  );

  const {data, isFetching, isLoading, isError} = useGetAvailableCarsQuery(
    isNotNil(priceReport)
      ? {
          id: priceReport.id,
          body: {
            size: PAGE_SIZE,
            query_price: priceReport.sellingPrice.recommended.value,
            offset: page !== null ? (page - 1) * PAGE_SIZE : undefined,
            ...filter,
          },
        }
      : skipToken,
    {skip: isNil(filter) || !hasServersSelected}
  );

  const myVehicleTranslatedInfo = useVehicleTranslatedInfo(priceReport?.vehicle);
  const availableCars = filterErrors || !hasServersSelected ? [] : (data?.result ?? []);

  const currentPage = data?.current_page ?? 0;
  const pagesQuantity = data?.total_pages ?? 0;
  const results = data?.total_hits ?? 0;

  if (!priceReport) {
    return null;
  }

  const myVehicle: AvailableCarWithLogo = {
    isMyVehicle: true,
    logoUrl: myVehicleTranslatedInfo?.data?.logoUrl ?? undefined,
    ...formatMyVehicle(priceReport),
  };

  const mappedNeighbours: AvailableCarWithLogo[] = isDebug
    ? (priceReport.neighborAdIds
        ?.map((id) => neighbours?.find((car) => car.ad_id === id))
        .filter((car): car is AvailableCar => isNotNil(car))
        .map((car) => ({
          ...car,
          isMyVehicle: false,
          isPricingNeighbour: true,
        })) ?? [])
    : [];

  const pageOffset = PAGE_SIZE * Math.max((currentPage ?? 1) - 1, 0);
  const queryOriginalPriceIndex = data?.query_original_price_index ?? 0;

  const mappedAvailableCars = availableCars.map((car, apiIndex) => ({
    ...car,
    isMyVehicle: false,
    isPricingNeighbour: false,
    marketPosition: getAvailableCarMarketPosition(apiIndex, queryOriginalPriceIndex, pageOffset),
  }));

  const neighbourAdIds = new Set(priceReport.neighborAdIds ?? []);
  const competitorCars: AvailableCarWithLogo[] = [
    ...mappedNeighbours,
    ...mappedAvailableCars.filter((car) => isNil(car.ad_id) || !neighbourAdIds.has(car.ad_id)),
  ];

  const competitorCarsWithMyVehicle: AvailableCarWithLogo[] = sortBy(
    (vehicle) => vehicle.original_price_with_vat ?? 0,
    [myVehicle, ...competitorCars]
  );

  const getVehiclePosition = (vehicle: AvailableCarWithLogo): number | Nullish => {
    if (vehicle.isPricingNeighbour) {
      return null;
    }
    if (vehicle.isMyVehicle) {
      return queryOriginalPriceIndex + 1;
    }
    return vehicle.marketPosition ?? null;
  };

  return (
    <>
      <Helmet title={i18n.t('page.advertisiments.title')} />
      <MobilePaginationWithFilterBar
        page={currentPage}
        onPageChange={setPage}
        pagesQuantity={pagesQuantity}
        results={results}
        isLoading={isFetching || isLoading}
      >
        <DirectionSwitch
          direction={direction}
          setDirection={setDirection}
          data-testid={suffixTestId('mobile', props)}
        />
      </MobilePaginationWithFilterBar>
      <Box padding={2}>
        <Box
          borderRadius={theme.components.Card.borderRadius}
          boxShadow={theme.components.Card.elevation}
          backgroundColor="general.white"
          padding={direction === 'column' ? 0 : 4}
          paddingTop={4}
        >
          <Box paddingHorizontal={direction === 'column' ? 4 : 0} paddingBottom={4}>
            <VehicleInfo
              catalogue={catalogue}
              vehicle={priceReport.vehicle}
              data-testid={suffixTestId('vehicleInfo', props)}
            >
              <DesktopPagination
                page={currentPage}
                onPageChange={setPage}
                pagesQuantity={pagesQuantity}
                results={results}
                data-testid={suffixTestId('desktop', props)}
              >
                <DirectionSwitch
                  direction={direction}
                  setDirection={setDirection}
                  data-testid={suffixTestId('desktop', props)}
                />
              </DesktopPagination>
            </VehicleInfo>
          </Box>
          <DataStatus
            isLoading={isFetching || isLoading || areNeighboursLoading}
            isError={isError}
            isEmpty={isEmpty(competitorCarsWithMyVehicle)}
            spacing={12}
          >
            {direction === 'row' || device === 'mobile' ? (
              <AdvertisementRowLayout
                vehicles={competitorCarsWithMyVehicle}
                data-testid={suffixTestId('rowLayout', props)}
              />
            ) : (
              <AdvertisementColumnLayout
                vehicles={competitorCarsWithMyVehicle}
                getVehiclePosition={getVehiclePosition}
                data-testid={suffixTestId('columnLayout', props)}
              />
            )}
          </DataStatus>
        </Box>
      </Box>
    </>
  );
}
